import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PayPalCheckout from '../components/PayPalCheckout';
import { CreditCard, Loader2, Lock, AlertCircle, Landmark } from 'lucide-react';
import { api, formatPrice } from '../lib/api';
import { US_STATES } from '../lib/shop';
import { useCart } from '../context/CartContext';
import useSEO from '../hooks/useSEO';

const EMPTY = { name: '', email: '', phone: '', company: '', address1: '', address2: '', city: '', state: '', zip: '', notes: '' };
const REQUIRED = ['name', 'email', 'phone', 'address1', 'city', 'state', 'zip'];
const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const Field = ({ label, k, form, setForm, type = 'text', span = '' }) => (
    <label className={`block ${span}`}>
        <span className="label-caps text-xs text-neutral-500">{label}{REQUIRED.includes(k) ? ' *' : ''}</span>
        <input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            className="mt-1 w-full border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 focus:outline-none focus:border-brand-primary"
            data-testid={`checkout-${k}`} />
    </label>
);

const Summary = ({ quote }) => (
    <div className="space-y-2 text-sm" data-testid="checkout-summary">
        {quote.lines.map((l) => (
            <div key={l.machine_id} className="flex justify-between gap-3 text-neutral-700"><span>{l.qty} × {l.name}</span><span>{money(l.line_total)}</span></div>
        ))}
        <div className="border-t border-neutral-200 pt-2 flex justify-between"><span>Subtotal</span><span data-testid="checkout-subtotal">{money(quote.subtotal)}</span></div>
        <div className="flex justify-between"><span>Shipping</span><span data-testid="checkout-shipping">{quote.shipping ? money(quote.shipping) : 'FREE'}</span></div>
        {quote.tax > 0 && <div className="flex justify-between"><span>{quote.tax_label}</span><span data-testid="checkout-tax">{money(quote.tax)}</span></div>}
        <div className="border-t border-neutral-300 pt-3 flex justify-between font-display font-black text-2xl text-neutral-900"><span>Total</span><span data-testid="checkout-total">{money(quote.total)}</span></div>
    </div>
);

const Checkout = () => {
    const { items, clear } = useCart();
    const [form, setForm] = useState(EMPTY);
    const [quote, setQuote] = useState(null);
    const [config, setConfig] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [params] = useSearchParams();
    const navigate = useNavigate();
    useSEO({ title: 'Checkout — Dry Ice Bulls', description: 'Secure checkout for dry ice cleaning accessories and parts.' });

    const cartItems = items.map((i) => ({ id: i.id, qty: i.qty }));

    useEffect(() => { api.get('/shop/config').then((r) => setConfig(r.data)); }, []);
    useEffect(() => {
        if (!items.length) return;
        api.post('/shop/quote', { items: cartItems, state: form.state })
            .then((r) => { setQuote(r.data); setError(''); })
            .catch((e) => setError(e.response?.data?.detail || 'Could not price your cart'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, form.state]);

    const missing = REQUIRED.filter((k) => !String(form[k]).trim());
    const valid = missing.length === 0 && /\S+@\S+\.\S+/.test(form.email);
    const requireValid = () => {
        if (valid) return true;
        setError('Please fill in all required fields with a valid email before paying.');
        return false;
    };
    const apiError = (e) => {
        const d = e.response?.data?.detail;
        setError(typeof d === 'string' ? d : 'Please check your details — some fields look incomplete.');
    };
    // PayPal buttons mount once; callbacks read the latest form through this ref.
    const latest = useRef({});
    latest.current = { form, valid, cartItems };

    const payOffline = async (method) => {
        if (!requireValid()) return;
        setBusy(method);
        setError('');
        try {
            const { data } = await api.post('/shop/checkout/offline', { items: cartItems, customer: form, method });
            clear();
            navigate(`/order/success?order=${data.order_id}`);
        } catch (e) { apiError(e); setBusy(false); }
    };

    if (!items.length) {
        return (
            <div className="container-page pt-32 pb-24" data-testid="checkout-page">
                <p className="text-neutral-600" data-testid="checkout-empty">Your cart is empty. <Link to="/shop" className="text-brand-primary font-bold">Shop equipment →</Link></p>
            </div>
        );
    }

    return (
        <div className="container-page pt-32 pb-24" data-testid="checkout-page">
            <div className="label-caps text-brand-primary mb-4">/ Secure checkout</div>
            <h1 className="font-display font-black uppercase tracking-tighter text-5xl md:text-6xl text-neutral-900">Checkout</h1>
            {params.get('cancelled') && <p className="mt-4 text-amber-700" data-testid="checkout-cancelled">Payment was cancelled — your cart is still here.</p>}

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <section className="lg:col-span-7">
                    <h2 className="font-display font-bold uppercase text-lg text-neutral-900 mb-4">Contact & shipping</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Full name" k="name" form={form} setForm={setForm} />
                        <Field label="Company" k="company" form={form} setForm={setForm} />
                        <Field label="Email" k="email" type="email" form={form} setForm={setForm} />
                        <Field label="Phone" k="phone" type="tel" form={form} setForm={setForm} />
                        <Field label="Street address" k="address1" form={form} setForm={setForm} span="sm:col-span-2" />
                        <Field label="Apt, suite (optional)" k="address2" form={form} setForm={setForm} span="sm:col-span-2" />
                        <Field label="City" k="city" form={form} setForm={setForm} />
                        <div className="grid grid-cols-2 gap-4">
                            <label className="block">
                                <span className="label-caps text-xs text-neutral-500">State *</span>
                                <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                                    className="mt-1 w-full border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900" data-testid="checkout-state">
                                    <option value="">—</option>
                                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </label>
                            <Field label="ZIP" k="zip" form={form} setForm={setForm} />
                        </div>
                        <label className="block sm:col-span-2">
                            <span className="label-caps text-xs text-neutral-500">Order notes (optional)</span>
                            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                className="mt-1 w-full border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900" data-testid="checkout-notes" />
                        </label>
                    </div>
                </section>

                <aside className="lg:col-span-5 border border-neutral-200 bg-neutral-50 p-6 h-fit lg:sticky lg:top-28">
                    <h2 className="font-display font-bold uppercase text-lg text-neutral-900 mb-4">Order summary</h2>
                    {quote ? <Summary quote={quote} /> : <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />}
                    {quote && quote.shipping > 0 && quote.free_shipping_threshold > 0 && (
                        <p className="mt-3 text-xs text-neutral-500">Free shipping on orders over {formatPrice(quote.free_shipping_threshold)}.</p>
                    )}
                    {error && <p className="mt-4 text-sm text-red-600 flex gap-2" data-testid="checkout-error"><AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{error}</p>}

                    <div className="mt-6 grid gap-3">
                        <button onClick={() => payOffline('invoice')} disabled={!!busy || !quote}
                            className="w-full inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-white font-bold px-6 py-4 label-caps transition-colors"
                            data-testid="checkout-pay-invoice">
                            {busy === 'invoice' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Credit card or ACH (we email an invoice)
                        </button>
                        <button onClick={() => payOffline('wire')} disabled={!!busy || !quote}
                            className="w-full inline-flex items-center justify-center gap-2 border border-neutral-900 bg-white hover:bg-neutral-900 hover:text-white disabled:opacity-60 text-neutral-900 font-bold px-6 py-4 label-caps transition-colors"
                            data-testid="checkout-pay-wire">
                            {busy === 'wire' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Landmark className="h-4 w-4" />} Bank wire / ACH transfer
                        </button>
                        <p className="text-xs text-neutral-500">Your order is reserved right away and ships once payment is received.</p>
                    </div>

                    {config?.paypal_client_id && <div className="my-4 flex items-center gap-3 text-xs text-neutral-400 label-caps"><span className="h-px flex-1 bg-neutral-300" />or<span className="h-px flex-1 bg-neutral-300" /></div>}

                    <div data-testid="checkout-paypal">
                        {config?.paypal_client_id && quote ? (
                                <PayPalCheckout clientId={config.paypal_client_id}
                                    onClick={(_, actions) => {
                                        if (latest.current.valid) return actions.resolve();
                                        setError('Please fill in all required fields with a valid email before paying.');
                                        return actions.reject();
                                    }}
                                    createOrder={async () => {
                                        setError('');
                                        try {
                                            const { data } = await api.post('/shop/paypal/create-order', { items: latest.current.cartItems, customer: latest.current.form });
                                            return data.paypal_order_id;
                                        } catch (e) { apiError(e); throw e; }
                                    }}
                                    onApprove={async (data) => {
                                        try {
                                            const res = await api.post('/shop/paypal/capture', { paypal_order_id: data.orderID });
                                            clear();
                                            navigate(`/order/success?order=${res.data.order_id}`);
                                        } catch (e) { apiError(e); }
                                    }}
                                    onError={() => setError((prev) => prev || 'PayPal could not complete the payment. Please try again or pay by card.')}
                                />
                        ) : null}
                    </div>
                    <p className="mt-4 text-xs text-neutral-500 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> PayPal payments are processed securely by PayPal.</p>
                </aside>
            </div>
        </div>
    );
};

export default Checkout;
